import { useState } from 'react';

/**
 * Calendar view modes
 */
export type CalendarViewMode = 'week' | 'month';

/**
 * Week day data structure for mobile week view
 */
export interface WeekDayData {
  day: number;
  dayName: string;
  date: string;
}

/**
 * useCalendarLogic - Calendar date calculations and state management
 *
 * Provides:
 * - Current date state management
 * - Month navigation
 * - Week/month grid generation
 * - Selected date tracking
 * - View mode state (week/month)
 */
export function useCalendarLogic() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarViewMode, setCalendarViewMode] = useState<CalendarViewMode>('month');
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);

  /**
   * Get number of days in a specific month
   */
  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  /**
   * Navigate to previous/next month
   */
  const changeMonth = (increment: number) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + increment);
    setCurrentDate(newDate);
  };

  /**
   * Generate month grid as weeks array
   * Each week is an array of 7 days (null for empty cells)
   */
  const generateMonthGrid = (): (number | null)[][] => {
    const daysInMonth = getDaysInMonth(currentDate.getFullYear(), currentDate.getMonth());
    const startDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();

    const weeks: (number | null)[][] = [];
    let day = 1;

    for (let i = 0; i < 6; i++) {
      const week: (number | null)[] = [];
      for (let j = 0; j < 7; j++) {
        if ((i === 0 && j < startDay) || day > daysInMonth) {
          week.push(null);
        } else {
          week.push(day);
          day++;
        }
      }
      weeks.push(week);
    }

    return weeks;
  };

  /**
   * Get current week for mobile week view
   * Returns 7 days starting from Sunday of current week
   */
  const getCurrentWeek = (): WeekDayData[] => {
    const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    const today = new Date();
    const currentWeekDays: WeekDayData[] = [];

    // Get the start of the current week (Sunday)
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());

    // Generate 7 days from Sunday to Saturday
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);

      currentWeekDays.push({
        day: date.getDate(),
        dayName: weekDays[i],
        date: date.toISOString().split('T')[0]
      });
    }

    return currentWeekDays;
  };

  /**
   * Get week day names in Spanish
   */
  const getWeekDayNames = (): string[] => {
    return ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  };

  /**
   * Check if a specific day is today
   */
  const isToday = (day: number): boolean => {
    const today = new Date();
    return day === today.getDate() &&
           currentDate.getMonth() === today.getMonth() &&
           currentDate.getFullYear() === today.getFullYear();
  };

  /**
   * Format date as YYYY-MM-DD from day number
   */
  const formatDate = (day: number): string => {
    return new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toISOString().split('T')[0];
  };

  return {
    // State
    currentDate,
    calendarViewMode,
    selectedDate,

    // Setters
    setCurrentDate,
    setCalendarViewMode,
    setSelectedDate,

    // Computed values
    monthGrid: generateMonthGrid(),
    currentWeek: getCurrentWeek(),
    weekDayNames: getWeekDayNames(),
    daysInMonth: getDaysInMonth(currentDate.getFullYear(), currentDate.getMonth()),

    // Methods
    changeMonth,
    isToday,
    formatDate,
    getDaysInMonth,
  };
}
